import { useEffect, useState } from "react";
import GridLayout, { WidthProvider } from "react-grid-layout";
import { QrCode, Copy, Trash2 } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { fontStyleToCss, buttonStyleToCss, deriveWidgets, reconcileGrid, GRID_COLS, GRID_ROWS } from "@/lib/kioskGrid";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const RGL = WidthProvider(GridLayout);

const fmtTime = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const fmtDate = (d) => d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

function WidgetPreview({ wd, cfg, now, defaultLogo = "" }) {
  if (wd.kind === "logo") {
    const effLogo = ((cfg.logo_mode || "custom") !== "default" && cfg.logo_url) ? cfg.logo_url : (defaultLogo || cfg.logo_url || "");
    return effLogo ? (
      <img src={resolveImageUrl(effLogo)} alt="Logo" className="max-h-full max-w-full object-contain pointer-events-none" />
    ) : (
      <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center font-bold text-lg border border-[#E5E2DC]" style={{ color: cfg.accent_color }}>
        {(cfg.brand_name || "C").charAt(0)}
      </div>
    );
  }
  if (wd.kind === "brand") {
    return (
      <div className="min-w-0 text-left">
        <div className="leading-tight truncate font-bold" style={{ fontFamily: `'${cfg.heading_font?.family}', sans-serif`, color: cfg.heading_font?.color || "#2A2626", fontSize: "clamp(14px, 1.6vw, 22px)" }}>
          {cfg.brand_name || "Your Church"}
        </div>
        <div className="text-xs opacity-60 truncate">{cfg.welcome_subtext || "Welcome — we're so glad you're here."}</div>
      </div>
    );
  }
  if (wd.kind === "clock") {
    return (
      <div className="text-right leading-tight">
        <div className="font-semibold" style={{ fontSize: "clamp(14px,1.6vw,22px)", color: cfg.default_font?.color }}>{fmtTime(now)}</div>
        <div className="text-xs opacity-70">{fmtDate(now)}</div>
      </div>
    );
  }
  if (wd.kind === "featured") {
    const ev = (cfg.featured_events || []).filter((e) => e && e.visible !== false)[0];
    const ASPECT_MAP = { wide: "16 / 9", square: "1 / 1", banner: "1920 / 692" };
    const mode = (ev && ev.aspect_ratio) || "auto";
    const aspect = ASPECT_MAP[mode];
    const imgFit = mode === "auto" ? "object-contain" : "object-cover";
    return (
      <div className="w-full h-full flex items-start justify-center overflow-hidden">
        <div
          className={`rounded-xl overflow-hidden relative bg-[#E9E5DD] border border-[#E5E2DC] ${aspect ? "w-full" : "w-full h-full"}`}
          style={aspect ? { aspectRatio: aspect, maxHeight: "100%" } : undefined}
          data-aspect={mode}
        >
          {ev?.image_url ? (
            <img src={resolveImageUrl(ev.image_url)} alt="" className={`absolute inset-0 w-full h-full ${imgFit} pointer-events-none`} />
          ) : null}
          {ev?.show_text !== false && ev?.title ? (<>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
              <p className="text-[10px] uppercase tracking-widest opacity-80">Spotlight</p>
              <p className="font-bold text-sm truncate">{ev.title}</p>
            </div>
          </>) : null}
        </div>
      </div>
    );
  }
  if (wd.kind === "button") {
    return (
      <div className="w-full h-full flex items-stretch gap-2">
        <div className="flex-1 flex flex-col justify-center px-3 overflow-hidden" style={{ ...buttonStyleToCss(wd.btn?.style || cfg.button_style), fontSize: "clamp(12px,1.3vw,18px)", padding: "0 14px" }}>
          <span className="truncate leading-tight">{wd.btn?.label}</span>
          {wd.btn?.subtitle ? <span className="truncate text-[0.62em] font-normal opacity-80 normal-case leading-tight">{wd.btn.subtitle}</span> : null}
        </div>
        {wd.btn?.qr_enabled && (
          <div className="aspect-square h-full rounded-lg bg-white border border-[#E5E2DC] flex items-center justify-center shrink-0">
            <QrCode className="w-4 h-4 text-[#2A2626]" />
          </div>
        )}
      </div>
    );
  }
  return null;
}

/**
 * Primary visual editing canvas. Elements are directly selectable; the parent maps the
 * selected key to a contextual property panel. Drag/resize/snapping run on the internal
 * grid model (kept for rendering + backwards compatibility) — no grid terminology is
 * shown to users.
 */
export function GridEditor({ cfg, onChange, selectedKey, onSelect, onDuplicateCard, onDeleteCard, defaultLogo = "" }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  const widgets = deriveWidgets(cfg);
  const items = reconcileGrid(cfg).items;
  const layout = items.map((it) => ({ ...it, minW: 1, minH: 1, maxH: GRID_ROWS }));

  return (
    <div
      className="grid-editor-canvas rounded-2xl border border-[#E5E2DC] p-2 grain"
      style={{ backgroundColor: cfg.bg_color || "#FAF9F6" }}
      data-testid="grid-editor"
      onClick={(e) => { if (e.target === e.currentTarget && onSelect) onSelect("background"); }}
    >
      <style>{`.grid-editor-canvas .react-resizable-handle{z-index:15}`}</style>
      <RGL
        className="layout"
        layout={layout}
        cols={GRID_COLS}
        rowHeight={52}
        margin={[8, 8]}
        compactType={null}
        preventCollision={false}
        isBounded
        resizeHandles={["se"]}
        onDragStart={(l, oldItem) => onSelect && onSelect(oldItem.i)}
        onResizeStart={(l, oldItem) => onSelect && onSelect(oldItem.i)}
        onLayoutChange={(l) => onChange({ items: l.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })) })}
      >
        {widgets.map((wd) => {
          const selected = selectedKey === wd.key;
          return (
            <div
              key={wd.key}
              className={`group relative rounded-xl bg-white/70 ring-1 transition-shadow cursor-move flex items-center justify-center overflow-hidden p-1.5 ${selected ? "ring-2 ring-[#2A2626] shadow-md" : "ring-[#E5E2DC] hover:ring-2 hover:ring-[#2A2626]/50"}`}
              data-testid={`grid-widget-${wd.key}`}
            >
              {/* Descendant overlay: react-draggable overrides handlers only on its DIRECT child,
                  so this reliably fires selection on press while still bubbling to start a drag. */}
              <div className="absolute inset-0 z-[5]" onMouseDown={() => onSelect && onSelect(wd.key)} data-testid={`grid-select-${wd.key}`} />
              <span className={`absolute top-1 left-1 z-10 text-[9px] font-semibold uppercase tracking-wide bg-[#2A2626]/80 text-white px-1.5 py-0.5 rounded transition-opacity pointer-events-none ${selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                {wd.label}
              </span>
              {wd.kind === "button" && selected && (
                <span className="absolute top-1 right-1 z-20 flex gap-1">
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onDuplicateCard && onDuplicateCard(wd.idx); }}
                    className="w-6 h-6 rounded-md bg-white border border-[#E5E2DC] flex items-center justify-center hover:bg-[#F0EBE1]"
                    aria-label="Duplicate card"
                    data-testid={`grid-card-duplicate-${wd.idx}`}
                  >
                    <Copy className="w-3 h-3 text-[#2A2626]" />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onDeleteCard && onDeleteCard(wd.idx); }}
                    className="w-6 h-6 rounded-md bg-white border border-[#E5E2DC] flex items-center justify-center hover:bg-[#FBEBEA]"
                    aria-label="Delete card"
                    data-testid={`grid-card-delete-${wd.idx}`}
                  >
                    <Trash2 className="w-3 h-3 text-[#B23A48]" />
                  </button>
                </span>
              )}
              <div className="w-full h-full flex items-center justify-center">
                <WidgetPreview wd={wd} cfg={cfg} now={now} defaultLogo={defaultLogo} />
              </div>
            </div>
          );
        })}
      </RGL>
    </div>
  );
}
